import { useEffect, useRef, useState, type FC } from 'react';
import { InlineLoading, InlineNotification } from '@carbon/react';

import { ROTULOS, rotuloDoAviso } from './rotulos';
import styles from './LeitorDePdf.module.scss';

// O LEITOR DE PDF — as páginas desenhadas pelo pdf.js, uma embaixo da outra, na largura disponível
// (em dobro na tela de alta densidade, para não borrar). Não é um <iframe>: o leitor embutido do
// navegador abria vazio dentro do visualizador, e varia de navegador para navegador. Enquanto as
// páginas chegam, o `InlineLoading` diz qual; se o PDF não abre, um aviso de erro diz o que fazer.
//
// A página é papel: zona `white` do Carbon, também dentro do visualizador escuro.

const LARGURA_MAXIMA = 900;

export const LeitorDePdf: FC<{ url: string; nome: string }> = ({ url, nome }) => {
  const caixa = useRef<HTMLDivElement>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [desenhadas, setDesenhadas] = useState(0);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let cancelado = false;
    let tarefa: { destroy: () => Promise<void> } | null = null;
    const alvo = caixa.current;
    if (!alvo) return undefined;
    alvo.innerHTML = '';
    setTotal(null);
    setDesenhadas(0);
    setFalhou(false);

    (async () => {
      try {
        const { default: pdfjs } = await import('../../logica/arquivos/pdfjs');
        // ⚠️ `isEvalSupported: false` É SEGURANÇA, NÃO AJUSTE (QA dos Arquivos, 26/09/2026): com
        // `eval`, um PDF montado para isso roda JavaScript no domínio do app (CVE-2024-4367). O
        // pdf.js já é o 4.x (que corrige a falha); a flag fica como segunda tranca.
        const carregamento = pdfjs.getDocument({ url, isEvalSupported: false });
        tarefa = carregamento;
        const documento = await carregamento.promise;
        if (cancelado) return;
        setTotal(documento.numPages);
        const largura = Math.min(alvo.clientWidth || LARGURA_MAXIMA, LARGURA_MAXIMA);
        const densidade = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= documento.numPages; n += 1) {
          if (cancelado) return;
          const pagina = await documento.getPage(n);
          const escala = largura / pagina.getViewport({ scale: 1 }).width;
          const vista = pagina.getViewport({ scale: escala * densidade });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(vista.width);
          canvas.height = Math.floor(vista.height);
          canvas.style.width = `${Math.floor(vista.width / densidade)}px`;
          canvas.className = `cds--white ${styles.pagina}`;
          canvas.setAttribute('aria-label', `Página ${n} de ${documento.numPages}`);
          canvas.setAttribute('role', 'img');
          alvo.appendChild(canvas);
          const contexto = canvas.getContext('2d');
          if (!contexto) throw new Error('sem canvas');
          await pagina.render({ canvasContext: contexto, viewport: vista }).promise;
          if (!cancelado) setDesenhadas(n);
        }
      } catch {
        if (!cancelado) setFalhou(true);
      }
    })();

    return () => {
      cancelado = true;
      tarefa?.destroy().catch(() => undefined);
    };
  }, [url]);

  return (
    <div className={styles.leitor} aria-label={`Documento ${nome}`}>
      {falhou ? (
        <InlineNotification
          kind='error'
          lowContrast
          hideCloseButton
          title='Não deu para mostrar este PDF aqui.'
          subtitle='Baixe o arquivo ou abra numa aba nova.'
          statusIconDescription={rotuloDoAviso('error')}
          aria-label={ROTULOS.fecharAviso}
        />
      ) : (
        (total === null || desenhadas < total) && (
          <div className={styles.andamento}>
            <InlineLoading description={total === null ? 'Abrindo…' : `Página ${desenhadas + 1} de ${total}…`} iconDescription={ROTULOS.carregando} />
          </div>
        )
      )}
      <div ref={caixa} className={styles.paginas} />
    </div>
  );
};
